import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/common';
import { todayCalendarDate } from '@/lib/calendar-date';
import { useCurrencies } from '@/hooks/useCurrencies';
import { useBeneficiaryOptions } from '@/hooks/useBeneficiaryOptions';
import { NewOutgoingFormSchema, type NewOutgoingForm } from './types';
import { OperationField, fieldBorderClass } from './OperationField';

/** Sentinel for "charge the fee in the currency of the wire". */
export const FEE_SAME_AS_AMOUNT = '';

export interface OutgoingOperationFormProps {
  clientId: number | undefined;
  onSubmit: (data: NewOutgoingForm) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

export function OutgoingOperationForm({
  clientId,
  onSubmit,
  onCancel,
  isSubmitting,
}: OutgoingOperationFormProps) {
  const {
    codes: currencyCodes,
    unavailable: currenciesUnavailable,
    error: currencyError,
  } = useCurrencies();
  const {
    beneficiaries,
    isLoading: isLoadingBeneficiaries,
    isError: beneficiariesFailed,
  } = useBeneficiaryOptions(clientId);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting: isValidating },
  } = useForm<NewOutgoingForm>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zod 3/4 type mismatch with @hookform/resolvers
    resolver: zodResolver(NewOutgoingFormSchema as any),
    mode: 'onBlur',
    defaultValues: {
      valueDate: todayCalendarDate(),
      currencyCode: '',
      amount: '',
      fee: '',
      feeCurrencyCode: FEE_SAME_AS_AMOUNT,
      beneficiaryId: '',
      reference: '',
      description: '',
      sameClient: false,
    },
  });

  const currencyCode = watch('currencyCode') ?? '';
  const feeCurrencyCode = watch('feeCurrencyCode') ?? '';
  useEffect(() => {
    if (feeCurrencyCode && !currencyCodes.includes(feeCurrencyCode))
      setValue('feeCurrencyCode', '');
  }, [currencyCodes, feeCurrencyCode, setValue]);
  useEffect(() => {
    if (!currencyCodes.includes(currencyCode)) setValue('currencyCode', currencyCodes[0] ?? '');
  }, [currencyCodes, currencyCode, setValue]);

  useEffect(() => {
    setValue('clientId', clientId as number, { shouldValidate: false });
    // Le formulaire reste monte quand l'operateur change de client dans le
    // selecteur au-dessus. Garder le beneficiaire precedent proposait de
    // payer le client B au payee du client A — refuse par le serveur, mais
    // apres coup, et avec un nom qui n'est meme plus dans la liste.
    setValue('beneficiaryId', '', { shouldValidate: false });
  }, [clientId, setValue]);

  return (
    <form
      className="mt-6"
      onSubmit={handleSubmit((data) => {
        if (!currenciesUnavailable && currencyCodes.includes(data.currencyCode)) onSubmit(data);
      })}
      data-testid="outgoing-operation-form"
    >
      {currencyError && (
        <p role="alert" className="mb-4 text-sm text-red-700">
          Unable to load currencies. Please reload and try again.
        </p>
      )}
      <h4 className="mb-4 text-sm font-semibold text-[#333]">Outgoing payment</h4>

      <div className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">
        <OperationField id="out-date" label="Date" required error={errors.valueDate?.message}>
          <input
            id="out-date"
            type="date"
            {...register('valueDate')}
            className={fieldBorderClass(Boolean(errors.valueDate))}
            data-testid="outgoing-date"
          />
        </OperationField>

        <OperationField
          id="out-currency"
          label="Currency"
          required
          error={errors.currencyCode?.message}
        >
          <select
            id="out-currency"
            {...register('currencyCode')}
            disabled={currenciesUnavailable}
            className={fieldBorderClass(Boolean(errors.currencyCode))}
            data-testid="outgoing-currency"
          >
            <option value="" disabled>
              {currenciesUnavailable ? 'Currencies unavailable' : 'Select currency'}
            </option>
            {currencyCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </OperationField>

        <OperationField id="out-amount" label="Amount" required error={errors.amount?.message}>
          <input
            id="out-amount"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            {...register('amount')}
            className={fieldBorderClass(Boolean(errors.amount))}
            data-testid="outgoing-amount"
          />
        </OperationField>

        <OperationField id="out-fee" label="Fee (Montelux revenue)" error={errors.fee?.message}>
          <input
            id="out-fee"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            {...register('fee')}
            className={fieldBorderClass(Boolean(errors.fee))}
            data-testid="outgoing-fee"
          />
        </OperationField>

        <OperationField
          id="out-fee-currency"
          label="Fee currency"
          error={errors.feeCurrencyCode?.message}
        >
          <select
            id="out-fee-currency"
            {...register('feeCurrencyCode')}
            disabled={currenciesUnavailable}
            className={fieldBorderClass(Boolean(errors.feeCurrencyCode))}
            data-testid="outgoing-fee-currency"
          >
            <option value={FEE_SAME_AS_AMOUNT}>Same as amount</option>
            {currencyCodes.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </select>
        </OperationField>

        <div aria-hidden="true" className="hidden sm:block" />

        <OperationField
          id="out-beneficiary"
          label="Counterparty (beneficiary)"
          required
          error={
            beneficiariesFailed
              ? 'Could not load this client’s beneficiaries'
              : errors.beneficiaryId?.message
          }
        >
          <select
            id="out-beneficiary"
            disabled={clientId === undefined || isLoadingBeneficiaries || beneficiariesFailed}
            {...register('beneficiaryId')}
            className={fieldBorderClass(Boolean(errors.beneficiaryId))}
            data-testid="outgoing-beneficiary"
          >
            <option value="">
              {beneficiariesFailed
                ? 'Beneficiaries unavailable'
                : isLoadingBeneficiaries
                  ? 'Loading beneficiaries…'
                  : 'Select a beneficiary'}
            </option>
            {beneficiaries.map((b) => (
              <option key={b.id} value={b.id}>
                {b.label}
              </option>
            ))}
          </select>
        </OperationField>

        <OperationField id="out-reference" label="Reference" error={errors.reference?.message}>
          <input
            id="out-reference"
            type="text"
            maxLength={80}
            placeholder="optional"
            {...register('reference')}
            className={fieldBorderClass(Boolean(errors.reference))}
            data-testid="outgoing-reference"
          />
        </OperationField>
      </div>

      <label className="mt-4 flex items-center gap-2 text-xs text-[#666]">
        <input
          type="checkbox"
          {...register('sameClient')}
          className="h-3.5 w-3.5 rounded border-[#ccc] accent-[#6B0F1A]"
          data-testid="outgoing-same-client"
        />
        Same client (funds sent to own external account — no third-party counterparty)
      </label>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2">
        <OperationField
          id="out-description"
          label="Description"
          error={errors.description?.message}
        >
          <input
            id="out-description"
            type="text"
            maxLength={255}
            placeholder="optional"
            {...register('description')}
            className={fieldBorderClass(Boolean(errors.description))}
            data-testid="outgoing-description"
          />
        </OperationField>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button
          type="submit"
          disabled={currenciesUnavailable || isSubmitting || isValidating}
          data-testid="outgoing-submit"
        >
          {isSubmitting ? 'Booking…' : 'Book operation'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="outgoing-cancel">
          Cancel
        </Button>
      </div>
    </form>
  );
}
